'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalendarIcon,
  EditIcon,
  EyeIcon,
  FileIcon,
  ImageIcon,
  MoreHorizontalIcon,
  SquareMenuIcon,
  SquarePlusIcon,
  TagIcon,
  TextIcon,
  Trash2Icon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { SortEnum } from '@/enums'
import { useDeleteBrand, useBrands, useBulkDeleteBrands } from '@/hooks'
import { useBrandsStore, useMeStore } from '@/store'
import { hasPermission } from '@/lib/permissions'
import { BrandsFilterParams } from '@/types/brand'
import { DeleteConfirmationDialog, LoadingState, BrandsFilters, SortableHeader } from '@/components/partials'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { formatDateTime, getResizedPictureUrl } from '@/lib/utils'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { BulkDeleteResponse } from '@/types/general'
import Image from 'next/image'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const [deleteBrandId, setDeleteBrandId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  const { mutate: deleteBrandMutate, isPending: isDeleting } = useDeleteBrand()
  const { mutate: bulkDeleteBrandsMutate, isPending: isBulkDeleting } = useBulkDeleteBrands()

  const { meState } = useMeStore()
  const currentUser = meState.response
  const canCreate = hasPermission(currentUser, 'BRANDS', 'CREATE')
  const canShow = hasPermission(currentUser, 'BRANDS', 'SHOW')
  const canUpdate = hasPermission(currentUser, 'BRANDS', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'BRANDS', 'DELETE')

  const selectedBrandIds = useBrandsStore(state => state.selectedBrandIds)
  const toggleBrandSelection = useBrandsStore(state => state.toggleBrandSelection)
  const selectAllBrands = useBrandsStore(state => state.selectAllBrands)
  const deselectAllBrands = useBrandsStore(state => state.deselectAllBrands)

  const filters = useMemo<BrandsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.ASC,
      sortBy: params.get('sortBy') || 'name',
      q: params.get('q') || '',
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined,
      updatedAtStart: params.get('updatedAtStart') || undefined,
      updatedAtEnd: params.get('updatedAtEnd') || undefined
    }
  }, [searchParams])
  const { data: brandsData, isLoading, error, refetch, isFetching } = useBrands(filters)

  // Update URL when filters change
  const updateUrlParams = (newFilters: Partial<BrandsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<BrandsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/products/brands')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy, sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (brandId: string) => {
    router.push(`/products/brands/${brandId}`)
  }

  const handleEdit = (brandId: string) => {
    router.push(`/products/brands/${brandId}/edit`)
  }

  const handleDelete = (brandId: string) => {
    setDeleteBrandId(brandId)
  }

  const renderActionItems = (brandId: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        {canShow && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleView(brandId)
            }}>
            <EyeIcon className="h-4 w-4" />
            {t('brand_details')}
          </ItemComponent>
        )}

        {canUpdate && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleEdit(brandId)
            }}>
            <EditIcon className="h-4 w-4" />
            {t('edit_brand')}
          </ItemComponent>
        )}
        {canDelete && (
          <ItemComponent
            onClick={e => {
              e.stopPropagation()
              handleDelete(brandId)
            }}
            className="text-destructive focus:text-destructive">
            <Trash2Icon className="h-4 w-4" />
            {t('delete')}
          </ItemComponent>
        )}
      </>
    )
  }

  const confirmDeleteMessage = () => {
    if (deleteBrandId) {
      deleteBrandMutate(deleteBrandId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['brands'] })
          toast.success(t('deleted_successfully'))
          setDeleteBrandId(null)
        },
        onError: (error: any) => {
          console.error('Error deleting model:', error)
          toast.error(error.response?.data.message ?? t('error_deleting'))
          setDeleteBrandId(null)
        }
      })
    }
  }

  const handleBulkDelete = () => {
    if (selectedBrandIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const confirmBulkDeleteBrands = () => {
    if (selectedBrandIds.length > 0) {
      bulkDeleteBrandsMutate(selectedBrandIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['brands'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response) {
            if (error.response.status === 422) {
              toast.error(error.response.data?.items?.ids || t('error_deleting'))
            } else {
              toast.error(t('error_deleting'))
            }
          } else {
            toast.error(t('error_deleting'))
          }

          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const handleSelectAll = () => {
    if (brandsData && brandsData.items.length > 0) {
      if (selectedBrandIds.length === brandsData.items.length) {
        // If all brands are selected, deselect all
        deselectAllBrands()
      } else {
        // Otherwise, select all brands
        selectAllBrands(brandsData.items)
      }
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TagIcon className="h-5 w-5" />
                {t('brands')}
              </CardTitle>
              <CardDescription>{t('brands_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {canCreate && (
                <Button asChild>
                  <Link href="/products/brands/create">
                    <SquarePlusIcon />
                    <span className="hidden items-center gap-2 lg:flex">{t('create_brand')}</span>
                  </Link>
                </Button>
              )}
              {canDelete && selectedBrandIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedBrandIds.length})</span>
                  </div>
                </Button>
              )}
              <BrandsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {brandsData && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            {canDelete && (
                              <Checkbox
                                checked={
                                  brandsData &&
                                  brandsData.items.length > 0 &&
                                  selectedBrandIds.length === brandsData.items.length
                                }
                                onCheckedChange={() => {
                                  handleSelectAll()
                                }}
                                onClick={e => {
                                  e.stopPropagation()
                                }}
                                className="cursor-pointer"
                              />
                            )}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-12 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <ImageIcon className="h-4 w-4" />
                            {t('picture')}
                          </div>
                        </TableHead>
                        <TableHead className="min-w-50">
                          <SortableHeader
                            sortBy="name"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <TextIcon className="h-4 w-4" />
                            {t('name')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('created_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-50 text-center">
                          <SortableHeader
                            sortBy="updatedAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="mr-2 w-4" />
                            {t('updated_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {brandsData.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        brandsData.items.map((brand, index) => (
                          <ContextMenu key={brand.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => canShow && handleView(brand.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    {canDelete && (
                                      <Checkbox
                                        checked={selectedBrandIds.includes(brand.id)}
                                        onCheckedChange={() => toggleBrandSelection(brand.id)}
                                        onClick={e => {
                                          e.stopPropagation()
                                        }}
                                        className="cursor-pointer"
                                      />
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="flex min-h-20 items-center justify-center py-4">
                                  {brand.picture ? (
                                    <Image
                                      src={getResizedPictureUrl(brand.picture.path, 60, 60)}
                                      alt={brand.picture.name || ''}
                                      width={60}
                                      height={60}
                                      className="mx-auto max-h-15 w-auto max-w-15 border object-contain"
                                    />
                                  ) : (
                                    <div className="flex justify-center">
                                      <FileIcon className="text-muted-foreground h-10 w-10" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>{brand.name}</span>
                                    <span className="text-muted-foreground text-xs">ID: {brand.id}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(brand.createdAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-muted-foreground text-sm">
                                    <span>{formatDateTime(brand.updatedAt, locale)}</span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={e => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                        }}>
                                        <span className="sr-only">{t('open_menu')}</span>
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(brand.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(brand.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={brandsData.page}
                  totalPages={brandsData.pages}
                  totalItems={brandsData.total}
                  pageSize={brandsData.size}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      {canDelete && (
        <DeleteConfirmationDialog
          open={deleteBrandId !== null}
          onCancel={() => setDeleteBrandId(null)}
          onConfirm={confirmDeleteMessage}
          isDeleting={isDeleting}
        />
      )}

      {canDelete && (
        <DeleteConfirmationDialog
          open={bulkDeleteConfirmOpen}
          onCancel={() => setBulkDeleteConfirmOpen(false)}
          onConfirm={confirmBulkDeleteBrands}
          isDeleting={isBulkDeleting}
        />
      )}
    </>
  )
}

export default List
